import api from 'config/api.config';
import { AxiosResponse } from 'axios';

// Define interfaces for API response data
interface UserWithPayment {
  user_id: string;
  name: string;
  email: string;
  signup_date: string;
  subscription_type: string;
  subscription_start: string | null;
  subscription_end: string | null;
  monthly_price: number | null;
  last_payment_date: string | null;
  last_payment_amount: number | null;
  payment_status: string | null;
  converted_from: boolean | null;
}

interface StripeCheckoutSessionResponse {
  success: boolean;
  sessionId: string;
  url: string;
  customerId: string;
  message: string;
  // Mid-trial plan switch path returns mode:'plan_switched' + trialEnd, with no url —
  // the backend updates Stripe directly so no checkout redirect is needed.
  mode?: 'plan_switched';
  trialEnd?: string;
}

interface CreateCheckoutSessionPayload {
  subscriptionId: string;
  stripePriceId: string;
  billingCycle: 'monthly' | 'annual';
}

interface UsersWithPaymentsResponse {
  data: UserWithPayment[];
  pagination: {
    current_page: number;
    per_page: number;
    total_pages: number;
    total_items: number;
  };
}

interface CurrentStats {
  totalAccounts: number;
  conversionRate: number;
  currentMRR: number;
  activePaidAccounts: number;
}

interface Trends {
  newAccounts: number;
  conversionRate: number;
  revenue: number;
  paidAccounts: number;
}

interface WeeklyStats {
  weekStarting: string;
  trialAccounts: number;
  paidAccounts: {
    starter: number;
  };
  revenue: number;
  conversionRate: number;
}

interface RevenueBySub {
  name: string;
  value: number;
}

interface UsersOverview {
  totalUsers: number;
  activeSubscriptions: number;
  convertedFromTrial: number;
  starterUsers: number;
  trialUsers: number;
}

// Fetch users with their latest payment information (admin only)
export const getUsersWithPayments = async (
  page: number = 1,
  limit: number = 10
): Promise<UsersWithPaymentsResponse> => {
  try {
    const response: AxiosResponse<UsersWithPaymentsResponse> = await api.get(
      '/api/admin/payments',
      {
        params: { page, limit },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch users with payments'
    );
  }
};

// Fetch current stats for dashboard cards (admin only)
export const getCurrentStats = async (): Promise<CurrentStats> => {
  try {
    const response: AxiosResponse<CurrentStats> = await api.get(
      '/api/admin/stats/current'
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch current stats'
    );
  }
};

// Fetch trends for dashboard cards (admin only)
export const getTrends = async (): Promise<Trends> => {
  try {
    const response: AxiosResponse<Trends> = await api.get(
      '/api/admin/stats/trends'
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch trends'
    );
  }
};

// Fetch weekly stats for charts (admin only)
export const getWeeklyStats = async (): Promise<WeeklyStats[]> => {
  try {
    const response: AxiosResponse<WeeklyStats[]> = await api.get(
      '/api/admin/stats/weekly'
    );

    // Validate revenue is a number
    response.data.forEach((week, index) => {
      if (typeof week.revenue !== 'number' || isNaN(week.revenue)) {
        console.warn(`Invalid revenue at index ${index}: ${week.revenue}`);
        week.revenue = 0;
      }
    });

    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch weekly stats'
    );
  }
};

// Fetch revenue by subscription type (admin only)
export const getRevenueBySub = async (): Promise<RevenueBySub[]> => {
  try {
    const response: AxiosResponse<RevenueBySub[]> = await api.get(
      '/api/admin/stats/revenue-by-sub'
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch revenue by subscription type'
    );
  }
};

// Fetch users overview stats (admin only)
export const getUsersOverview = async (): Promise<UsersOverview> => {
  try {
    const response: AxiosResponse<UsersOverview> = await api.get(
      '/api/admin/stats/users-overview'
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch users overview stats'
    );
  }
};

// Create Stripe checkout session for subscription upgrade
export const createStripeCheckoutSession = async (
  subscriptionId: string,
  stripePriceId: string,
  billingCycle: 'monthly' | 'annual'
): Promise<StripeCheckoutSessionResponse> => {
  try {
    const payload: CreateCheckoutSessionPayload = {
      subscriptionId,
      stripePriceId,
      billingCycle,
    };

    const response: AxiosResponse<StripeCheckoutSessionResponse> = await api.post(
      '/api/subscription/upgrade-subscription',
      payload
    );

    return response.data;
  } catch (error: any) {
    throw error;
  }
};
